/**
 * Smart Odontogram™ — Interactive SVG odontogram (FDI numbering).
 * 32 adult teeth · 8 statuses · click to open side panel · audit timeline.
 *
 * Designed mobile-first / touch-friendly.
 * Inspirations: Apple Health, Stripe dashboards, modern US dental software.
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Loader2, X, Sparkles, ShieldCheck, Clock, User as UserIcon,
  Activity, AlertTriangle, Save, History,
} from "lucide-react";

/* FDI numbering, two arches × two halves.
 * Top arch: right→left  18 17 16 15 14 13 12 11  21 22 23 24 25 26 27 28
 * Bottom:   right→left  48 47 46 45 44 43 42 41  31 32 33 34 35 36 37 38
 */
const TOP_ROW = [18,17,16,15,14,13,12,11, 21,22,23,24,25,26,27,28];
const BOTTOM_ROW = [48,47,46,45,44,43,42,41, 31,32,33,34,35,36,37,38];

const STATE_META = {
  healthy:    { label: "Sano",         color: "#e2e8f0", text: "#334155", border: "#cbd5e1", emoji: "✓"  },
  cavity:     { label: "Carie",        color: "#fee2e2", text: "#b91c1c", border: "#fca5a5", emoji: "•" },
  filling:    { label: "Otturazione",  color: "#dbeafe", text: "#1e40af", border: "#93c5fd", emoji: "▣" },
  crown:      { label: "Corona",       color: "#fef3c7", text: "#92400e", border: "#fcd34d", emoji: "◆" },
  bridge:     { label: "Ponte",        color: "#ede9fe", text: "#5b21b6", border: "#c4b5fd", emoji: "≣" },
  implant:    { label: "Impianto",     color: "#dcfce7", text: "#166534", border: "#86efac", emoji: "▾" },
  root_canal: { label: "Devitalizzato",color: "#ffedd5", text: "#9a3412", border: "#fdba74", emoji: "▼" },
  extracted:  { label: "Estratto",     color: "#fef2f2", text: "#7f1d1d", border: "#fecaca", emoji: "✕" },
  missing:    { label: "Assente",      color: "#f1f5f9", text: "#475569", border: "#cbd5e1", emoji: "—" },
};

const QUADRANT_LABEL = (n) => {
  const q = Math.floor(n / 10);
  return q === 1 ? "↘ alto dx" : q === 2 ? "↙ alto sx" : q === 3 ? "↖ basso sx" : "↗ basso dx";
};

const SmartOdontogram = ({ clientId, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // tooth_number selected
  const [editing, setEditing] = useState({});       // local edit buffer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dental/odontogram/${clientId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento odontogramma");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const teethMap = useMemo(() => {
    const m = {};
    (data?.teeth || []).forEach(x => { m[x.tooth_number] = x; });
    return m;
  }, [data]);

  const counts = useMemo(() => {
    const c = {};
    (data?.teeth || []).forEach(x => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, [data]);

  const canEdit = data?.permissions?.edit_odontogram;

  const openTooth = (tn) => {
    setSelected(tn);
    const cur = teethMap[tn] || {};
    setEditing({
      status: cur.status || "healthy",
      notes: cur.notes || "",
      treatment_name: "",
      surfaces: cur.surfaces || [],
    });
  };

  const saveTooth = async () => {
    if (!selected || !canEdit) return;
    try {
      await axios.patch(
        `${API}/dental/odontogram/${clientId}/tooth/${selected}`,
        editing,
        { withCredentials: true },
      );
      toast.success(`Dente ${selected} aggiornato`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  if (loading || !data) {
    return (
      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="smart-odontogram">
      {/* Header pill */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-cyan-600 to-violet-600 text-white text-[9px] font-bold uppercase tracking-[0.18em]">
            <Sparkles className="w-2.5 h-2.5" /> Smart Odontogram™
          </span>
          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider">
            <ShieldCheck className="w-2.5 h-2.5 inline mr-0.5" /> GDPR · Audit Log
          </span>
        </div>
        <Legend counts={counts} />
      </div>

      {/* SVG board */}
      <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900/60 dark:to-slate-950/60 p-6 shadow-sm">
        <SvgArch row={TOP_ROW} arch="top" teethMap={teethMap} onPick={openTooth} />
        <div className="my-2 mx-auto w-32 h-px bg-slate-200 dark:bg-white/10" />
        <SvgArch row={BOTTOM_ROW} arch="bottom" teethMap={teethMap} onPick={openTooth} />
        <p className="text-center text-[10px] text-muted-foreground mt-4 font-mono tracking-wider">
          NUMERAZIONE FDI · CLICCA UN DENTE PER MODIFICARE
        </p>
      </div>

      {/* Side panel (modal on mobile, sliding panel on desktop) */}
      {selected && (
        <ToothPanel
          toothNumber={selected}
          tooth={teethMap[selected]}
          editing={editing}
          setEditing={setEditing}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onSave={saveTooth}
          clientId={clientId}
        />
      )}
    </div>
  );
};

/* ----------------------- SVG arch ----------------------- */
const SvgArch = ({ row, arch, teethMap, onPick }) => {
  // Simple grid: 16 columns, each 56×80 box, gap 4. Total width 968.
  const TOOTH_W = 56;
  const TOOTH_H = 78;
  const GAP = 4;
  const PADDING_X = 12;
  const totalW = row.length * TOOTH_W + (row.length - 1) * GAP + PADDING_X * 2;
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <svg
        viewBox={`0 0 ${totalW} ${TOOTH_H + 28}`}
        width="100%"
        style={{ minWidth: 760, maxWidth: 980 }}
        role="group"
        aria-label={arch === "top" ? "Arcata superiore" : "Arcata inferiore"}
      >
        {row.map((tn, i) => {
          const x = PADDING_X + i * (TOOTH_W + GAP);
          const y = arch === "top" ? 22 : 4;
          const tooth = teethMap[tn] || { status: "healthy" };
          const meta = STATE_META[tooth.status] || STATE_META.healthy;
          const labelY = arch === "top" ? 14 : TOOTH_H + 18;
          return (
            <g
              key={tn}
              transform={`translate(${x}, 0)`}
              onClick={() => onPick(tn)}
              style={{ cursor: "pointer" }}
              data-testid={`tooth-${tn}`}
            >
              <text
                x={TOOTH_W / 2}
                y={labelY}
                textAnchor="middle"
                fontSize={10}
                fontWeight={700}
                fill="#94a3b8"
                fontFamily="ui-monospace, SFMono-Regular, monospace"
              >
                {tn}
              </text>
              <Tooth
                x={0} y={y}
                w={TOOTH_W} h={TOOTH_H}
                fill={meta.color}
                stroke={meta.border}
                arch={arch}
              />
              <text
                x={TOOTH_W / 2}
                y={y + TOOTH_H / 2 + 4}
                textAnchor="middle"
                fontSize={14}
                fontWeight={800}
                fill={meta.text}
              >
                {meta.emoji}
              </text>
              {/* Update dot */}
              {tooth.updated_at && (
                <circle cx={TOOTH_W - 6} cy={y + 6} r={3} fill="#22c55e">
                  <title>Modificato il {new Date(tooth.updated_at).toLocaleDateString("it-IT")}</title>
                </circle>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const Tooth = ({ x, y, w, h, fill, stroke, arch }) => {
  // Stylized molar/incisor silhouette. Cusp on the chewing side, root tapered.
  const r = 10;
  const top = arch === "top";
  // Build a path: rounded rect with bulged cusps on the bottom (top arch) or top (bottom arch)
  const cuspDepth = 6;
  const path = top
    ? `
      M ${x + r} ${y}
      L ${x + w - r} ${y}
      Q ${x + w} ${y} ${x + w} ${y + r}
      L ${x + w} ${y + h - 16}
      Q ${x + w * 0.85} ${y + h - 16 + cuspDepth} ${x + w * 0.7} ${y + h - 16}
      Q ${x + w / 2} ${y + h - 16 + cuspDepth + 3} ${x + w * 0.3} ${y + h - 16}
      Q ${x + w * 0.15} ${y + h - 16 + cuspDepth} ${x} ${y + h - 16}
      L ${x} ${y + r}
      Q ${x} ${y} ${x + r} ${y}
      Z
    `
    : `
      M ${x} ${y + 16}
      Q ${x + w * 0.15} ${y + 16 - cuspDepth} ${x + w * 0.3} ${y + 16}
      Q ${x + w / 2} ${y + 16 - cuspDepth - 3} ${x + w * 0.7} ${y + 16}
      Q ${x + w * 0.85} ${y + 16 - cuspDepth} ${x + w} ${y + 16}
      L ${x + w} ${y + h - r}
      Q ${x + w} ${y + h} ${x + w - r} ${y + h}
      L ${x + r} ${y + h}
      Q ${x} ${y + h} ${x} ${y + h - r}
      Z
    `;
  return (
    <path
      d={path}
      fill={fill}
      stroke={stroke}
      strokeWidth={1.5}
      style={{ transition: "fill 200ms, stroke 200ms" }}
    />
  );
};

/* ----------------------- Legend ----------------------- */
const Legend = ({ counts }) => (
  <div className="flex items-center flex-wrap gap-1.5" data-testid="odontogram-legend">
    {Object.entries(STATE_META).map(([k, m]) => {
      const c = counts[k] || 0;
      if (k === "healthy" && c === 0) return null;
      return (
        <div key={k} className="inline-flex items-center gap-1 rounded-full border border-slate-200/70 dark:border-white/10 px-2 py-0.5 text-[10px]">
          <span className="w-2 h-2 rounded-full" style={{ background: m.color, border: `1px solid ${m.border}` }} />
          <span className="font-semibold text-slate-700 dark:text-slate-200">{m.label}</span>
          {c > 0 && <span className="text-muted-foreground font-mono">{c}</span>}
        </div>
      );
    })}
  </div>
);

/* ----------------------- Side panel ----------------------- */
const ToothPanel = ({ toothNumber, tooth, editing, setEditing, canEdit, onClose, onSave, clientId }) => {
  const meta = STATE_META[editing.status] || STATE_META.healthy;
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setHistoryLoading(true);
      try {
        const r = await axios.get(
          `${API}/dental/odontogram/${clientId}/tooth/${toothNumber}/history`,
          { withCredentials: true },
        );
        if (alive) setHistory(r.data?.history || []);
      } catch { /* ignore */ } finally {
        if (alive) setHistoryLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [toothNumber, clientId]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="tooth-panel">
      <div
        className="flex-1 bg-black/40 backdrop-blur-sm animate-in fade-in"
        onClick={onClose}
        aria-label="Close"
      />
      <aside className="w-full max-w-md bg-white dark:bg-slate-950 border-l border-slate-200 dark:border-white/10 flex flex-col shadow-2xl animate-in slide-in-from-right">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-white/10 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300 mb-1">
              Dente {toothNumber} · {QUADRANT_LABEL(toothNumber)}
            </p>
            <h3 className="text-xl font-black tracking-tight">
              <span className="px-2 py-0.5 rounded-md text-base mr-2" style={{ background: meta.color, color: meta.text, border: `1px solid ${meta.border}` }}>
                {meta.emoji}
              </span>
              {meta.label}
            </h3>
            {tooth?.last_operator_name && (
              <p className="text-[11px] text-muted-foreground mt-1">
                <UserIcon className="w-3 h-3 inline mr-0.5" />
                Ultima modifica: {tooth.last_operator_name} ·{" "}
                {tooth.updated_at ? new Date(tooth.updated_at).toLocaleString("it-IT") : "—"}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
            data-testid="tooth-panel-close"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Status picker */}
          <section>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Stato</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(STATE_META).map(([k, m]) => {
                const active = editing.status === k;
                return (
                  <button
                    key={k}
                    onClick={() => canEdit && setEditing({ ...editing, status: k })}
                    disabled={!canEdit}
                    data-testid={`tooth-state-${k}`}
                    className={`rounded-xl px-2 py-2 text-[11px] font-bold transition-all border ${
                      active ? "ring-2 ring-violet-500 ring-offset-1 dark:ring-offset-slate-950 scale-[1.02]" : ""
                    } ${canEdit ? "" : "opacity-60 cursor-not-allowed"}`}
                    style={{ background: m.color, color: m.text, borderColor: m.border }}
                  >
                    <span className="mr-1">{m.emoji}</span> {m.label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Treatment + notes */}
          {canEdit && (
            <>
              <section>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Trattamento</label>
                <input
                  type="text"
                  value={editing.treatment_name || ""}
                  onChange={(e) => setEditing({ ...editing, treatment_name: e.target.value })}
                  data-testid="tooth-panel-treatment"
                  placeholder="Es. Otturazione 16-D"
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                />
              </section>
              <section>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Note cliniche</label>
                <textarea
                  rows={3}
                  value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="tooth-panel-notes"
                  placeholder="Annotazioni aggiuntive…"
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-3 py-2 text-sm leading-relaxed"
                />
              </section>
            </>
          )}

          {/* History */}
          <section>
            <div className="flex items-center gap-1.5 mb-2">
              <History className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Cronologia</p>
              <span className="ml-auto text-[10px] text-muted-foreground">{history.length} eventi</span>
            </div>
            {historyLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">Nessuna modifica registrata.</p>
            ) : (
              <ul className="space-y-2" data-testid="tooth-panel-history">
                {history.map((h, i) => {
                  const m = STATE_META[h.metadata?.status] || STATE_META.healthy;
                  return (
                    <li key={i} className="flex gap-3 rounded-xl border border-slate-100 dark:border-white/5 p-3">
                      <span
                        className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: m.color, color: m.text, border: `1px solid ${m.border}` }}
                      >
                        {m.emoji}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold">
                          {h.metadata?.treatment_name || m.label}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          <UserIcon className="w-2.5 h-2.5 inline mr-0.5" /> {h.actor_name}
                          <Clock className="w-2.5 h-2.5 inline ml-2 mr-0.5" />
                          {new Date(h.created_at).toLocaleString("it-IT")}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {canEdit && (
          <div className="px-5 py-4 border-t border-slate-200 dark:border-white/10">
            <button
              onClick={onSave}
              data-testid="tooth-panel-save"
              className="w-full rounded-full py-2.5 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 text-white hover:shadow-2xl inline-flex items-center justify-center gap-1.5 transition-all"
            >
              <Save className="w-4 h-4" /> Salva modifiche
            </button>
          </div>
        )}
        {!canEdit && (
          <div className="px-5 py-4 border-t border-slate-200 dark:border-white/10 bg-amber-50 dark:bg-amber-950/30">
            <p className="text-xs text-amber-800 dark:text-amber-200 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Permesso `edit_odontogram` mancante: sola lettura.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
};

export default SmartOdontogram;
